import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '@/services/api';
import { subscribeToChannel } from '@/services/ablyRealtime';

/**
 * GameScorePanel — customer-side live scoreboard for a Game/Sports venue.
 *
 * Shows the live game session on the customer's seated table and (when the backend authorizes the
 * caller as a seated player) lets them keep score. Reads the session on mount, then stays live via
 * the per-table Ably channel `game_session:<tableId>` (events `session:state` / `session:ended`)
 * with a light 6s poll fallback while a session is live — mirroring the other seated-table panels.
 *
 * Theme-aware via CSS tokens (var(--bg-card)/--text-primary/--accent-color): white for registered
 * users, dark for the QR portal.
 */

// ---- Session dto (backend contract) --------------------------------------------------------------
interface Player { name?: string | null }
interface FramesScore { kind: 'frames'; framesWon: [number, number]; currentPoints: [number, number]; target?: number | null; history?: any[] }
interface PointsScore { kind: 'points'; setsWon: [number, number]; currentPoints: [number, number]; target?: number | null }
interface ChessScore { kind: 'chess'; result: null | 'p1' | 'p2' | 'draw' }
type GameScore = FramesScore | PointsScore | ChessScore;
interface GameSession {
  id: string;
  branchId: string;
  tableId: string;
  tableName?: string | null;
  gameType?: string | null;
  status: 'live' | 'finished';
  players: Player[];
  score: GameScore;
  startedAt?: string | null;
  endedAt?: string | null;
}

interface Props {
  branchId: string;
  bookingId: string;
  tableId: string;
  tableName?: string;
  /** Rendered as a floating modal card (used from TableSelection's seated actions). */
  asModal?: boolean;
  onClose?: () => void;
}

const nameOf = (players: Player[] | undefined, i: number, fallback: string) =>
  (players && players[i] && players[i].name) ? String(players[i].name) : fallback;

const isPermissionError = (e: any) => {
  const status = e?.status;
  const msg = String(e?.message || '').toLowerCase();
  return status === 403 || msg.includes('permission') || msg.includes('not allowed') || msg.includes('forbidden') || msg.includes('only players');
};

const GameScorePanel: React.FC<Props> = ({ tableId, tableName, asModal, onClose }) => {
  const [session, setSession] = useState<GameSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false); // caller isn't authorized to score (read-only)
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const load = useCallback(async () => {
    try {
      const res = await apiService.getCurrentGameSession();
      if (!mounted.current) return;
      setSession(((res as any)?.data as GameSession) || null);
    } catch {
      // non-fatal: keep whatever we had; realtime/poll will recover
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  // Initial fetch.
  useEffect(() => { load(); }, [load]);

  // Live updates on the per-table channel.
  useEffect(() => {
    if (!tableId) return;
    const unsub = subscribeToChannel(
      `game_session:${tableId}`,
      ['session:state', 'session:ended'] as unknown as readonly string[],
      (_event, data) => {
        if (!data?.id) return;
        if (!mounted.current) return;
        setSession(data as GameSession);
      }
    );
    return () => { try { unsub(); } catch { /* noop */ } };
  }, [tableId]);

  // Poll fallback every ~6s while a session is live (in case an Ably event was missed).
  const isLive = session?.status === 'live';
  useEffect(() => {
    if (!isLive) return;
    let cancelled = false;
    const iv = setInterval(async () => {
      try {
        const res = await apiService.getCurrentGameSession();
        if (cancelled || !mounted.current) return;
        const s = ((res as any)?.data as GameSession) || null;
        if (s) setSession(s);
      } catch { /* non-fatal */ }
    }, 6000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [isLive]);

  const act = useCallback(async (action: any) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiService.sendGameAction(action);
      const s = ((res as any)?.data as GameSession) || null;
      if (mounted.current && s) setSession(s);
      if (mounted.current) setDenied(false);
    } catch (e: any) {
      if (!mounted.current) return;
      if (isPermissionError(e)) {
        setDenied(true);
      } else {
        setError(e?.message || 'Could not update the score. Please try again.');
      }
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [busy]);

  const S = STYLES;

  // ---- No session ------------------------------------------------------------------------------
  if (!loading && !session) {
    if (asModal) {
      return (
        <div style={S.overlay} onClick={onClose}>
          <div style={S.card} onClick={(e) => e.stopPropagation()}>
            <Header tableName={tableName} onClose={onClose} />
            <div style={S.empty}>No game in play</div>
          </div>
        </div>
      );
    }
    return <div style={S.inlineEmpty}>No game in play</div>;
  }

  if (loading && !session) {
    const body = <div style={S.empty}>Loading…</div>;
    return asModal
      ? <div style={S.overlay} onClick={onClose}><div style={S.card} onClick={(e) => e.stopPropagation()}><Header tableName={tableName} onClose={onClose} />{body}</div></div>
      : <div style={S.inlineCard}>{body}</div>;
  }

  const finished = session!.status === 'finished';
  const canScore = !finished && !denied;
  const score = session!.score;

  const body = (
    <>
      <div style={S.venueHint}>📺 Live on the venue screen</div>

      {score?.kind === 'frames' && (
        <FramesView session={session!} />
      )}
      {score?.kind === 'points' && (
        <PointsView session={session!} />
      )}
      {score?.kind === 'chess' && (
        <ChessView session={session!} />
      )}

      {finished && <div style={S.finishedTag}>Game finished</div>}

      {denied && !finished && (
        <div style={S.note}>Only players at this table can score.</div>
      )}
      {error && <div style={{ ...S.note, color: '#dc2626' }}>{error}</div>}

      {canScore && score?.kind === 'frames' && (
        <Controls
          players={session!.players}
          busy={busy}
          awardLabel="Frame"
          onPoint={(p) => act({ type: 'points', player: p, delta: 1 })}
          onAward={(p) => act({ type: 'frame', player: p })}
          onUndo={() => act({ type: 'undo' })}
        />
      )}
      {canScore && score?.kind === 'points' && (
        <Controls
          players={session!.players}
          busy={busy}
          awardLabel="Set"
          onPoint={(p) => act({ type: 'points', player: p, delta: 1 })}
          onAward={(p) => act({ type: 'set', player: p })}
          onUndo={() => act({ type: 'undo' })}
        />
      )}
      {canScore && score?.kind === 'chess' && (
        <ChessControls busy={busy} onResult={(v) => act({ type: 'result', value: v })} />
      )}
    </>
  );

  if (asModal) {
    return (
      <div style={S.overlay} onClick={onClose}>
        <div style={S.card} onClick={(e) => e.stopPropagation()}>
          <Header tableName={session!.tableName || tableName} gameType={session!.gameType} onClose={onClose} />
          {body}
        </div>
      </div>
    );
  }
  return (
    <div style={S.inlineCard}>
      <Header tableName={session!.tableName || tableName} gameType={session!.gameType} />
      {body}
    </div>
  );
};

// ---- Sub-views -----------------------------------------------------------------------------------
const Header: React.FC<{ tableName?: string | null; gameType?: string | null; onClose?: () => void }> = ({ tableName, gameType, onClose }) => (
  <div style={STYLES.header}>
    <div>
      <div style={STYLES.title}>🎯 Live score</div>
      <div style={STYLES.subtitle}>{[tableName, gameType].filter(Boolean).join(' · ') || 'Scoreboard'}</div>
    </div>
    {onClose && <button aria-label="Close" style={STYLES.closeBtn} onClick={onClose}>✕</button>}
  </div>
);

const Scoreboard: React.FC<{ players: Player[]; a: React.ReactNode; b: React.ReactNode; sep?: string; subA?: React.ReactNode; subB?: React.ReactNode }>
  = ({ players, a, b, sep = '—', subA, subB }) => (
  <div style={STYLES.board}>
    <div style={STYLES.side}>
      <div style={STYLES.pname}>{nameOf(players, 0, 'Player A')}</div>
      <div style={STYLES.big}>{a}</div>
      {subA != null && <div style={STYLES.sub}>{subA}</div>}
    </div>
    <div style={STYLES.sep}>{sep}</div>
    <div style={STYLES.side}>
      <div style={STYLES.pname}>{nameOf(players, 1, 'Player B')}</div>
      <div style={STYLES.big}>{b}</div>
      {subB != null && <div style={STYLES.sub}>{subB}</div>}
    </div>
  </div>
);

const FramesView: React.FC<{ session: GameSession }> = ({ session }) => {
  const s = session.score as FramesScore;
  const fw = s.framesWon || [0, 0];
  const cp = s.currentPoints || [0, 0];
  return (
    <>
      <Scoreboard
        players={session.players}
        a={fw[0]} b={fw[1]}
        subA={<>frame: {cp[0]}</>} subB={<>frame: {cp[1]}</>}
      />
      <div style={STYLES.metaRow}>
        <span>Frames{s.target ? ` · first to ${s.target}` : ''}</span>
      </div>
    </>
  );
};

const PointsView: React.FC<{ session: GameSession }> = ({ session }) => {
  const s = session.score as PointsScore;
  const sw = s.setsWon || [0, 0];
  const cp = s.currentPoints || [0, 0];
  return (
    <>
      <Scoreboard
        players={session.players}
        a={sw[0]} b={sw[1]}
        subA={<>points: {cp[0]}</>} subB={<>points: {cp[1]}</>}
      />
      <div style={STYLES.metaRow}>
        <span>Sets{s.target ? ` · first to ${s.target}` : ''}</span>
      </div>
    </>
  );
};

const ChessView: React.FC<{ session: GameSession }> = ({ session }) => {
  const s = session.score as ChessScore;
  let text = 'In progress';
  if (s.result === 'p1') text = `${nameOf(session.players, 0, 'Player A')} won`;
  else if (s.result === 'p2') text = `${nameOf(session.players, 1, 'Player B')} won`;
  else if (s.result === 'draw') text = 'Draw';
  return (
    <div style={STYLES.chessBoard}>
      <div style={STYLES.pname}>{nameOf(session.players, 0, 'Player A')} vs {nameOf(session.players, 1, 'Player B')}</div>
      <div style={STYLES.chessResult}>{text}</div>
    </div>
  );
};

const Controls: React.FC<{
  players: Player[];
  busy: boolean;
  awardLabel: string;
  onPoint: (player: 0 | 1) => void;
  onAward: (player: 0 | 1) => void;
  onUndo: () => void;
}> = ({ players, busy, awardLabel, onPoint, onAward, onUndo }) => (
  <div style={STYLES.controls}>
    {[0, 1].map((p) => (
      <div key={p} style={STYLES.ctrlCol}>
        <div style={STYLES.ctrlName}>{nameOf(players, p, p === 0 ? 'Player A' : 'Player B')}</div>
        <button disabled={busy} style={STYLES.pointBtn} onClick={() => onPoint(p as 0 | 1)}>+1 point</button>
        <button disabled={busy} style={STYLES.awardBtn} onClick={() => onAward(p as 0 | 1)}>{awardLabel}</button>
      </div>
    ))}
    <button disabled={busy} style={STYLES.undoBtn} onClick={onUndo}>↩ Undo</button>
  </div>
);

const ChessControls: React.FC<{ busy: boolean; onResult: (v: 'p1' | 'p2' | 'draw') => void }> = ({ busy, onResult }) => (
  <div style={STYLES.controls}>
    <button disabled={busy} style={STYLES.awardBtn} onClick={() => onResult('p1')}>A wins</button>
    <button disabled={busy} style={STYLES.awardBtn} onClick={() => onResult('draw')}>Draw</button>
    <button disabled={busy} style={STYLES.awardBtn} onClick={() => onResult('p2')}>B wins</button>
  </div>
);

// ---- Styles (inline, token-driven so they adapt to app-white / QR-dark) --------------------------
const STYLES: Record<string, React.CSSProperties> = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  card: { width: '100%', maxWidth: 380, background: 'var(--bg-card)', color: 'var(--text-primary)', borderRadius: 16, border: '1px solid var(--border, rgba(0,0,0,0.08))', padding: 16, boxShadow: '0 12px 40px rgba(0,0,0,0.3)' },
  inlineCard: { background: 'var(--bg-card)', color: 'var(--text-primary)', borderRadius: 14, border: '1px solid var(--border, rgba(0,0,0,0.08))', padding: 14, marginTop: 8 },
  inlineEmpty: { color: 'var(--text-secondary, #64748b)', fontSize: '0.8rem', padding: '6px 2px', marginTop: 8 },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 },
  title: { fontWeight: 800, fontSize: '1rem' },
  subtitle: { fontSize: '0.72rem', color: 'var(--text-secondary, #64748b)', marginTop: 2 },
  closeBtn: { background: 'transparent', border: 'none', color: 'var(--text-secondary, #64748b)', fontSize: '1.1rem', cursor: 'pointer', lineHeight: 1, padding: 4 },
  venueHint: { fontSize: '0.68rem', color: 'var(--accent-color)', fontWeight: 700, marginBottom: 8 },
  board: { display: 'flex', alignItems: 'stretch', justifyContent: 'space-between', gap: 8, background: 'var(--bg-primary, rgba(0,0,0,0.03))', borderRadius: 12, padding: '12px 8px' },
  side: { flex: 1, textAlign: 'center', minWidth: 0 },
  pname: { fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  big: { fontSize: '2.2rem', fontWeight: 900, lineHeight: 1.1, color: 'var(--accent-color)' },
  sub: { fontSize: '0.7rem', color: 'var(--text-secondary, #64748b)', marginTop: 2 },
  sep: { display: 'flex', alignItems: 'center', fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-secondary, #94a3b8)' },
  metaRow: { display: 'flex', justifyContent: 'center', fontSize: '0.7rem', color: 'var(--text-secondary, #64748b)', marginTop: 8 },
  chessBoard: { textAlign: 'center', background: 'var(--bg-primary, rgba(0,0,0,0.03))', borderRadius: 12, padding: '14px 8px' },
  chessResult: { fontSize: '1.2rem', fontWeight: 900, color: 'var(--accent-color)', marginTop: 6 },
  finishedTag: { textAlign: 'center', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary, #64748b)', marginTop: 8 },
  note: { fontSize: '0.75rem', color: 'var(--text-secondary, #64748b)', textAlign: 'center', marginTop: 8 },
  controls: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12, alignItems: 'flex-start', justifyContent: 'center' },
  ctrlCol: { display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 120 },
  ctrlName: { fontSize: '0.72rem', fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  pointBtn: { padding: '8px 10px', borderRadius: 10, border: 'none', background: 'var(--accent-color)', color: '#fff', fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer' },
  awardBtn: { padding: '8px 10px', borderRadius: 10, border: '1px solid var(--accent-color)', background: 'transparent', color: 'var(--accent-color)', fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer' },
  undoBtn: { padding: '8px 12px', borderRadius: 10, border: '1px solid var(--border, rgba(0,0,0,0.15))', background: 'transparent', color: 'var(--text-primary)', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', flexBasis: '100%' },
  empty: { textAlign: 'center', color: 'var(--text-secondary, #64748b)', fontSize: '0.85rem', padding: '18px 8px' },
};

export default GameScorePanel;
